import { useEffect, useMemo, useState } from 'react';
import ModalCustom from '../../components/utils/ModalCustom.js';
import ShowUserDetails from './ShowUserDetails.js';

const PAGE_SIZE = 25;
const DEFAULT_BALANCE_TO_HIDE = 500;
const CREDITS_PER_PAGE = 10;

const EXCLUDED_FROM_TOTAL_EMAILS = new Set([
    'incidents@extracta.ai',
    'barzoiu.alexandru@gmail.com',
    'alexraducu03@gmail.com',
]);

function balanceToPages(balance) {
    const n = Number(balance);
    if (Number.isNaN(n)) return 0;
    return n / CREDITS_PER_PAGE;
}

function formatPages(value) {
    const n = Number(value);
    if (Number.isNaN(n)) return '0';
    return n.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 0 });
}

export default function UserCreditsBalancesAdmin({ usersInfo = {}, creditBalancesByUserId = {} }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [hideDefaultBalance, setHideDefaultBalance] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const allRows = useMemo(() => {
        return Object.entries(creditBalancesByUserId).map(([userId, balance]) => {
            const numericBalance = Number(balance) || 0;
            const email = usersInfo[userId]?.email || '';
            const pages = balanceToPages(numericBalance);
            return { userId, balance: numericBalance, pages, email };
        });
    }, [creditBalancesByUserId, usersInfo]);

    const filteredRows = useMemo(() => {
        const query = debouncedSearchTerm.trim().toLowerCase();
        return allRows.filter((row) => {
            if (hideDefaultBalance && row.balance === DEFAULT_BALANCE_TO_HIDE) {
                return false;
            }
            if (!query) return true;
            return (
                row.userId.toLowerCase().includes(query) ||
                row.email.toLowerCase().includes(query)
            );
        });
    }, [allRows, debouncedSearchTerm, hideDefaultBalance]);

    const sortedRows = useMemo(() => {
        return [...filteredRows].sort((a, b) => b.pages - a.pages);
    }, [filteredRows]);

    const totalPagesSum = useMemo(() => {
        return sortedRows.reduce((sum, row) => {
            const email = (row.email || '').toLowerCase();
            if (EXCLUDED_FROM_TOTAL_EMAILS.has(email)) {
                return sum;
            }
            return sum + row.pages;
        }, 0);
    }, [sortedRows]);

    const paginationPageCount = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
    const indexOfLast = currentPage * PAGE_SIZE;
    const indexOfFirst = indexOfLast - PAGE_SIZE;
    const pageRows = sortedRows.slice(indexOfFirst, indexOfLast);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchTerm, hideDefaultBalance]);

    useEffect(() => {
        if (currentPage > paginationPageCount) {
            setCurrentPage(paginationPageCount);
        }
    }, [currentPage, paginationPageCount]);

    const openUser = (userId) => {
        setSelectedUserId(userId);
        setOpen(true);
    };

    return (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <ModalCustom open={open} setOpen={setOpen}>
                <ShowUserDetails
                    userId={selectedUserId}
                    closeModal={() => {
                        setOpen(false);
                        setSelectedUserId('');
                    }}
                />
            </ModalCustom>

            <div className="flex flex-col gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:px-6">
                <input
                    type="text"
                    placeholder="Search by email or user ID"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full max-w-md rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:flex-1"
                />
                <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                    <input
                        type="checkbox"
                        checked={hideDefaultBalance}
                        onChange={(e) => setHideDefaultBalance(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    Hide {DEFAULT_BALANCE_TO_HIDE}-credit accounts
                </label>
            </div>

            <div className="flex flex-col gap-1 border-b border-gray-200 px-4 py-3 text-sm text-gray-600 sm:flex-row sm:flex-wrap sm:items-baseline sm:gap-x-4 sm:px-6">
                <span>
                    Showing {sortedRows.length} of {allRows.length} users
                </span>
                <span>
                    Total pages:{' '}
                    <span className="text-base font-semibold tabular-nums text-gray-900">
                        {formatPages(totalPagesSum)}
                    </span>
                    <span className="ml-1 text-xs text-gray-500">(excludes internal accounts)</span>
                </span>
            </div>

            <div className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 sm:px-6">
                <span>
                    Page {currentPage} of {paginationPageCount}
                </span>
                <div className="flex gap-1">
                    <button
                        type="button"
                        disabled={currentPage <= 1}
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        className="rounded-md border border-gray-300 bg-white px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Previous
                    </button>
                    <button
                        type="button"
                        disabled={currentPage >= paginationPageCount}
                        onClick={() => setCurrentPage((p) => Math.min(paginationPageCount, p + 1))}
                        className="rounded-md border border-gray-300 bg-white px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Next
                    </button>
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                                Email
                            </th>
                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                User ID
                            </th>
                            <th scope="col" className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">
                                Pages
                            </th>
                            <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                                <span className="sr-only">Actions</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                        {pageRows.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="py-8 text-center text-sm text-gray-500">
                                    No users match the current filters.
                                </td>
                            </tr>
                        ) : (
                            pageRows.map((row) => (
                                <tr key={row.userId}>
                                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-900 sm:pl-6">
                                        {row.email || '—'}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-4 font-mono text-xs text-gray-600">
                                        {row.userId}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-4 text-right text-sm font-semibold tabular-nums text-gray-900">
                                        {formatPages(row.pages)}
                                    </td>
                                    <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm sm:pr-6">
                                        <button
                                            type="button"
                                            onClick={() => openUser(row.userId)}
                                            className="font-medium text-indigo-600 hover:text-indigo-800"
                                        >
                                            Details
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
